// NewContact.jsx
// Route: /addcontact
// Add to router: <Route path="/addcontact" element={<NewContact />} />

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center" style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }} onClick={() => setOpen(!open)}>
        <span>{title}</span><span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

const ADDRESS_TYPES = ["Billing", "Shipping", "Home", "Work", "Other"];
const emptyAddress = () => ({ addressType: "Home", addressLine1: "", addressLine2: "", city: "", state: "", country: "", postalCode: "" });

const initialForm = {
  customerId: "",
  name: "",
  email: "",
  phone: "",
  designation: "",
  department: "",
  addresses: [],
  notes: "",
  isPrimary: false,
};

export default function NewContact() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedCustomerId = searchParams.get("customerId") || "";
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({ ...initialForm, customerId: preselectedCustomerId });
  const [customers, setCustomers] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("address");
  const [navbarColor, setNavbarColor] = useState("#080235");

  const refs = useRef({});
  const setRef = (name) => (el) => { refs.current[name] = el; };

  // Bootstrap loader
  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const il = document.createElement("link"); il.rel = "stylesheet"; il.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(il);
    return () => { bl.remove(); il.remove(); };
  }, []);

  // Auth check
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) { toast.error("Unauthorized."); navigate("/"); return; }
  }, [navigate, token]);

  // Navbar color
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((d) => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  // Load all customers for the dropdown
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/customers/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : data.content || [];
        setCustomers(list.filter((c) => c.status === "Active"));
      })
      .catch(() => { });
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const handleAddressChange = (idx, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.addresses];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...prev, addresses: updated };
    });
  };
  const addAddress = () => setFormData((prev) => ({ ...prev, addresses: [...prev.addresses, emptyAddress()] }));
  const removeAddress = (idx) => setFormData((prev) => ({ ...prev, addresses: prev.addresses.filter((_, i) => i !== idx) }));

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const inputClass = (field) => {
    if (errors[field]) return "form-control is-invalid";
    if (touched[field] && !errors[field]) return "form-control is-valid";
    return "form-control";
  };
  const selectClass = (field) => {
    if (errors[field]) return "form-select is-invalid";
    if (touched[field] && !errors[field]) return "form-select is-valid";
    return "form-select";
  };

  const validate = () => {
    const e = {};
    if (!formData.customerId) e.customerId = "Please select a customer.";
    if (!formData.name.trim()) e.name = "Contact name is required.";
    if (!formData.email.trim()) e.email = "Email is required.";
    else if (!emailRegex.test(formData.email)) e.email = "Enter a valid email address.";
    return e;
  };

  const focusFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    const el = refs.current[firstKey];
    if (el?.focus) { el.focus(); el.scrollIntoView({ behavior: "smooth", block: "center" }); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validate();
    setErrors(newErrors);
    const touchedAll = {};
    Object.keys(initialForm).forEach((k) => (touchedAll[k] = true));
    setTouched(touchedAll);
    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      toast.error("Please fix form errors before submitting.");
      return;
    }
    setSaving(true);
    try {
      const { customerId, ...contactPayload } = formData;
      const res = await fetch(`${API_BASE_URL}/api/customers/${customerId}/contacts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(contactPayload),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success("Contact created successfully!", { onClose: () => navigate("/allcontacts") });
      setFormData(initialForm);
      setErrors({});
      setTouched({});
    } catch (err) {
      toast.error(err.message || "Failed to create contact.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AdminNavbar /></div>

      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* Breadcrumb */}
        <nav className="mb-2" style={{ fontSize: 13, color: "#888" }}>
          <Link to="/allcontacts" style={{ color: "#0052CC", textDecoration: "none" }}>Contacts</Link>
          <span className="mx-1">/</span>
          <span className="text-dark fw-semibold">New Contact</span>
        </nav>

        <div className="mb-3">
          <h4 className="m-0"><i className="bi bi-person-plus me-2"></i> New Contact</h4>
          <div className="d-flex justify-content-between align-items-center mt-2">
            <div>
              <button type="submit" form="newContactForm" className="btn btn-primary me-2" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
            </div>
            <Link to="/allcontacts" className="text-primary text-decoration-none me-3">List</Link>
          </div>
        </div>

        <form id="newContactForm" onSubmit={handleSubmit}>

          {/* Customer picker */}
          <Section title="Customer">
            <div className="col-md-4">
              <label className="form-label">Customer <span className="text-danger">*</span></label>
              <select ref={setRef("customerId")} name="customerId" className={selectClass("customerId")} value={formData.customerId} onChange={handleChange}>
                <option value="">— Select a customer —</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>{c.customerName} ({c.customerCode})</option>
                ))}
              </select>
              {errors.customerId && <div className="invalid-feedback">{errors.customerId}</div>}
              <div className="form-text">Only Active customers are shown.</div>
            </div>
            <div className="col-md-3 d-flex align-items-end">
              <div className="form-check">
                <input type="checkbox" className="form-check-input" id="isPrimary" name="isPrimary" checked={formData.isPrimary} onChange={handleChange} />
                <label className="form-check-label fw-semibold" htmlFor="isPrimary">Set as Primary Contact</label>
                <div className="form-text">Marks this as the main contact for the customer.</div>
              </div>
            </div>
          </Section>

          {/* Contact details */}
          <Section title="Contact Information">
            <div className="col-md-4">
              <label className="form-label">Full Name <span className="text-danger">*</span></label>
              <input ref={setRef("name")} type="text" name="name" className={inputClass("name")} value={formData.name} onChange={handleChange} placeholder="e.g. Rajesh Kumar" />
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Email Address <span className="text-danger">*</span></label>
              <input ref={setRef("email")} type="email" name="email" className={inputClass("email")} value={formData.email} onChange={handleChange} placeholder="email@company.com" />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label">Phone Number</label>
              <input type="tel" name="phone" className="form-control" value={formData.phone} onChange={handleChange} placeholder="+91 98765 43210" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Designation / Role</label>
              <input type="text" name="designation" className="form-control" value={formData.designation} onChange={handleChange} placeholder="e.g. Finance Manager" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Department</label>
              <input type="text" name="department" className="form-control" value={formData.department} onChange={handleChange} placeholder="e.g. Finance, HR" />
            </div>
          </Section>

        </form>

        {/* Tabs */}
        <ul className="nav nav-tabs mb-2" style={{ backgroundColor: navbarColor }}>
          {[["address", "Address"], ["notes", "Notes"]].map(([tabId, label]) => (
            <li className="nav-item" key={tabId}>
              <button type="button" className={`nav-link ${activeTab === tabId ? "active" : ""}`} onClick={() => setActiveTab(tabId)}
                style={{ color: activeTab === tabId ? navbarColor : "#fff", backgroundColor: activeTab === tabId ? "#fff" : "transparent", fontWeight: activeTab === tabId ? "600" : "normal" }}>
                {label}
              </button>
            </li>
          ))}
        </ul>

        <div className="border p-3 mb-3">
          {activeTab === "address" && (
            <Section title="Address">
              {formData.addresses.length === 0 && (
                <div className="col-12 text-muted small">No addresses added yet.</div>
              )}
              {formData.addresses.map((addr, idx) => (
                <div key={idx} className="col-12 border rounded p-0 overflow-hidden">
                  <div className="d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge bg-secondary">{addr.addressType || "Home"}</span>
                      <span className="text-muted small">Address {idx + 1}</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeAddress(idx)}>Remove</button>
                  </div>
                  <div className="row g-2 p-3">
                    <div className="col-md-3">
                      <label className="form-label">Address Type</label>
                      <select className="form-select" value={addr.addressType} onChange={(e) => handleAddressChange(idx, "addressType", e.target.value)}>
                        {ADDRESS_TYPES.map((t) => <option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="col-md-9">
                      <label className="form-label">Address Line 1</label>
                      <input type="text" className="form-control" value={addr.addressLine1} onChange={(e) => handleAddressChange(idx, "addressLine1", e.target.value)} placeholder="Street address" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Address Line 2</label>
                      <input type="text" className="form-control" value={addr.addressLine2} onChange={(e) => handleAddressChange(idx, "addressLine2", e.target.value)} placeholder="Apartment, suite, building (optional)" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">City</label>
                      <input type="text" className="form-control" value={addr.city} onChange={(e) => handleAddressChange(idx, "city", e.target.value)} placeholder="City" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">State / Province</label>
                      <input type="text" className="form-control" value={addr.state} onChange={(e) => handleAddressChange(idx, "state", e.target.value)} placeholder="State" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Country</label>
                      <input type="text" className="form-control" value={addr.country} onChange={(e) => handleAddressChange(idx, "country", e.target.value)} placeholder="Country" />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Postal Code</label>
                      <input type="text" className="form-control" value={addr.postalCode} onChange={(e) => handleAddressChange(idx, "postalCode", e.target.value)} placeholder="PIN / ZIP" />
                    </div>
                  </div>
                </div>
              ))}
              <div className="col-12">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={addAddress}>+ Add Address</button>
              </div>
            </Section>
          )}

          {activeTab === "notes" && (
            <Section title="Notes">
              <div className="col-md-12">
                <label className="form-label">Internal Notes</label>
                <textarea name="notes" className="form-control" rows={4} value={formData.notes} onChange={handleChange} placeholder="Any additional notes about this contact…" />
              </div>
            </Section>
          )}
        </div>

        <div className="d-flex align-items-center mt-2 mb-4">
          <button type="submit" form="newContactForm" className="btn btn-primary me-2" disabled={saving}>{saving ? "Saving…" : "Save"}</button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => navigate(-1)}>Cancel</button>
        </div>
      </div>
    </>
  );
}